/**
 * CroppedVideo — drop-in replacement for Remotion's <OffthreadVideo>
 * that applies a crop region and trim range sent from the Timeline app.
 *
 * Uses OffthreadVideo instead of Video to avoid browser-side delayRender
 * timeouts when remote media fails to load (code 4 errors).
 *
 * Crop format (matches Timeline's clip.transforms.crop):
 *   { x, y, width, height, sourceWidth, sourceHeight }
 *   All values in source-pixel coordinates.
 *   sourceWidth and sourceHeight are required for cropping.
 *
 * Trim props:
 *   startFrom  – source offset in frames
 *   endAt      – source end frame
 *
 * If no crop is provided (or sourceWidth/sourceHeight missing),
 * renders a normal cover-fit <OffthreadVideo>.
 *
 * Technique: identical to CroppedImg — renders the video oversized
 * inside an overflow:hidden container, positioned so only the crop
 * region is visible.
 *
 * All extra props (muted, loop, playbackRate, onError, etc.)
 * are forwarded directly to <OffthreadVideo>.
 */

import React from "react";
import { OffthreadVideo } from "remotion";
import { resolveMediaSrc } from "../utils/mediaSrc";

/**
 * @param {string}  src              – video URL
 * @param {object}  [crop]           – { x, y, width, height, sourceWidth, sourceHeight }
 * @param {number}  [startFrom]      – trim start in frames
 * @param {number}  [endAt]          – trim end in frames
 * @param {object}  [style]          – extra styles on the <OffthreadVideo>
 * @param {object}  [containerStyle] – extra styles on the wrapper div
 * @param {object}  rest             – forwarded to <OffthreadVideo> (muted, loop, etc.)
 */
export default function CroppedVideo({
  src,
  crop,
  startFrom,
  endAt,
  style = {},
  containerStyle = {},
  ...rest
}) {
  const safeOnError = rest.onError ?? (() => {});
  if (!src || typeof src !== "string") return null;

  const resolvedSrc = resolveMediaSrc(src);
  if (!resolvedSrc) return null;

  const sourceWidth = crop?.sourceWidth || 0;
  const sourceHeight = crop?.sourceHeight || 0;

  const hasCrop =
    crop &&
    crop.width > 0 &&
    crop.height > 0 &&
    sourceWidth > 0 &&
    sourceHeight > 0 &&
    // Not a full-frame "crop" (no-op)
    !(
      crop.x === 0 &&
      crop.y === 0 &&
      crop.width === sourceWidth &&
      crop.height === sourceHeight
    );

  // Build trim props object (only include if defined)
  const trimProps = {};
  if (typeof startFrom === 'number' && startFrom > 0) trimProps.startFrom = startFrom;
  if (typeof endAt === 'number' && endAt > 0) trimProps.endAt = endAt;

  if (!hasCrop) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          ...containerStyle,
        }}
      >
        <OffthreadVideo
          src={resolvedSrc}
          onError={safeOnError}
          {...trimProps}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            ...style,
          }}
          {...rest}
        />
      </div>
    );
  }

  // Scale so that crop region fills the visible container
  const imgW = (sourceWidth / crop.width) * 100;
  const imgH = (sourceHeight / crop.height) * 100;
  const offX = -(crop.x / crop.width) * 100;
  const offY = -(crop.y / crop.height) * 100;

  // Strip geometry props from caller style so they can't override crop math
  const { width: _w, height: _h, left: _l, top: _t, position: _p, objectFit: _o, ...safeStyle } = style;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        position: "relative",
        ...containerStyle,
      }}
    >
      <OffthreadVideo
        src={resolvedSrc}
        onError={safeOnError}
        {...trimProps}
        style={{
          position: "absolute",
          width: `${imgW}%`,
          height: `${imgH}%`,
          left: `${offX}%`,
          top: `${offY}%`,
          objectFit: "cover",
          ...safeStyle,
        }}
        {...rest}
      />
    </div>
  );
}

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
